import { yupResolver } from '@hookform/resolvers/yup';
import {
    ChevronRightOutlined as ChevronRight,
    DarkModeOutlined as DarkIcon,
    LightModeOutlined as LightIcon
} from '@mui/icons-material';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Snackbar,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import { lgb as logob, lgv as logov } from 'assets';
import { useAuthContext, useThemeContext } from 'contexts';
import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { object as YupObject, string as YupString } from 'yup';

const schema = YupObject().shape({
    email: YupString().email().required(),
    senha: YupString().required().min(8)
});

export const Login = ({ children }) => {
    const theme = useTheme();
    const { toggleTheme } = useThemeContext();
    const { isAuthenticated, isLoading, login } = useAuthContext();
    const [loginError, setLoginError] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            email: '',
            senha: ''
        },
        resolver: yupResolver(schema)
    });

    if (isLoading) {
        return (
            // Melhorar a exibição desta parte deixando mais transparente para o usuário
            <Box
                width='100vw'
                height='100vh'
                display='flex'
                justifyContent='center'
                alignItems='center'>
                <CircularProgress />
            </Box>
        );
    }

    if (isAuthenticated) {
        return <Fragment>{children}</Fragment>;
    }

    const onSubmit = async ({ email, senha }) => {
        setLoginError('');

        const result = await login(email, senha);
        if (result) {
            setLoginError(result);
            return;
        }
    };

    return (
        <Fragment>
            <AppBar
                elevation={0}
                sx={{ zIndex: theme.zIndex.modal, bgcolor: '#D97706' }}>
                <Toolbar sx={{ height: theme.spacing(9) }}>
                    <Box
                        component='img'
                        src={logob}
                        alt='The Fish Club'
                        width={180}
                        sx={{ opacity: 0.85 }}
                    />
                    <Typography
                        variant='h6'
                        component='div'
                        sx={{ flexGrow: 1 }}>
                        {/* The Fish Club */}
                    </Typography>
                    <Tooltip title='Alterar Tema'>
                        <IconButton
                            color='inherit'
                            onClick={toggleTheme}>
                            {theme.palette.mode === 'light' ? <DarkIcon /> : <LightIcon />}
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Box
                width='100vw'
                height='100vh'
                display='flex'
                justifyContent='center'
                alignItems='center'>
                <Card>
                    <CardContent>
                        <Box
                            component='form'
                            onSubmit={handleSubmit(onSubmit)}
                            display='flex'
                            flexDirection='column'
                            gap={2}
                            padding={2}
                            width={450}>
                            <Box
                                display='flex'
                                justifyContent='center'
                                alignItems='center'>
                                <Box
                                    component='img'
                                    src={logov}
                                    alt='The Fish Club'
                                    width={300}
                                />
                            </Box>
                            <Typography
                                variant='body2'
                                paragraph
                                align='center'>
                                Digite sua senha para acessar o sistema administrativo de controle de pedidos e
                                gerenciamento de operações.
                            </Typography>

                            {loginError && (
                                <Snackbar
                                    open={Boolean(loginError)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    autoHideDuration={3000}
                                    onClose={() => setLoginError(null)}>
                                    <Alert
                                        severity='error'
                                        variant='filled'
                                        onClose={() => setLoginError(null)}>
                                        {loginError}
                                    </Alert>
                                </Snackbar>
                            )}

                            <TextField
                                fullWidth
                                size='small'
                                label='Email'
                                type='email'
                                disabled={isSubmitting}
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                            <TextField
                                fullWidth
                                size='small'
                                label='Senha'
                                type='password'
                                disabled={isSubmitting}
                                {...register('senha')}
                                error={!!errors.senha}
                                helperText={errors.senha?.message}
                            />
                            <Button
                                fullWidth
                                variant='contained'
                                endIcon={
                                    isSubmitting ? (
                                        <CircularProgress
                                            variant='indeterminate'
                                            color='inherit'
                                            size={22}
                                        />
                                    ) : (
                                        <ChevronRight />
                                    )
                                }
                                type='submit'
                                disabled={isSubmitting}>
                                Entrar
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Fragment>
    );
};
